import React, { useMemo, useState } from 'react'
import { MagnifyingGlassIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'

const AccountTable = ({ accounts = [], onEdit, onDelete, bankLookup = new Map() }) => {
  const [search, setSearch] = useState('')
  const filteredAccounts = useMemo(() => {
    if (!search.trim()) return accounts

    const keyword = search.trim().toLowerCase()
    return accounts.filter((account) => {
      const bank = bankLookup.get(account.bankType) ?? {}
      return (
        account.accountName?.toLowerCase().includes(keyword) ||
        account.accountNumber?.toLowerCase().includes(keyword) ||
        bank.name?.toLowerCase().includes(keyword)
      )
    })
  }, [accounts, search, bankLookup])

  return (
    <section className="flex h-full flex-col rounded-3xl border border-customSurface/10 bg-white p-6 shadow-xl shadow-slate-900/5 dark:border-customSurface dark:bg-customSurface">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-xl font-semibold text-customInk dark:text-customHeading">
            Saved payment accounts
          </h3>
          {/* <p className="text-sm text-customSecondaryText dark:text-customMuted">
            Manage the destinations your finance team uses to approve balance transfers.
          </p> */}
        </div>
        <div className="relative w-full max-w-xs">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
          <input
            type="text"
            value={search}
            placeholder="Search account or bank"
            onChange={(event) => setSearch(event.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-3 text-sm text-customPrimaryText focus:border-customAccent focus:outline-none focus:ring-2 focus:ring-customAccent/30 dark:border-white/10 dark:bg-customSurfaceMuted dark:text-customHeading"
          />
        </div>
      </header>

      <div className="mt-6 grow overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-y-3">
          <thead>
            <tr className="bg-background text-left text-xs uppercase tracking-wide text-customLightMilkWhite dark:text-customMuted">
              <th className="px-4 py-2">Bank</th>
              <th className="px-4 py-2">Account Info</th>
              {/* <th className="px-4 py-2">Account number</th> */}
              <th className="px-4 py-2">Created on</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 px-6 py-10 text-center text-sm text-slate-500 dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/60 dark:text-customMuted"
                >
                  No accounts matched your search. Try a different keyword or create a new account.
                </td>
              </tr>
            ) : (
              filteredAccounts.map((account) => {
                const bank = bankLookup.get(account.bankType) ?? account.bankType
                console.log('bank', bankLookup)
                return (
                  <tr
                    key={account.id ?? account.accountNumber}
                    className="rounded-3xl bg-slate-50/80 text-sm shadow-sm transition hover:bg-slate-100 dark:bg-customSurfaceMuted/60 dark:text-customHeading dark:hover:bg-customSurfaceMuted"
                  >
                    <td className="rounded-l-3xl px-4 py-2">
                      <div className="flex items-center gap-3">
                        {bank?.icon && (
                          <img
                            src={bank.icon}
                            alt={bank.name}
                            className="h-9 w-9 rounded-full border border-white/70 object-contain shadow-sm"
                          />
                        )}
                        <div>
                          <p className="font-medium">{bank?.name ?? '—'}</p>
                          {bank?.description && (
                            <span className="text-xs text-slate-500 dark:text-customMuted">
                              {bank.description}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="font-semibold text-customPrimaryText dark:text-customHeading">
                        {account.accountName}
                      </div>
                      {account.accountNumber && (
                        <div className="text-xs text-customSecondaryText dark:text-customMuted">
                          {account.accountNumber}
                        </div>
                      )}
                    </td>
                    {/* <td className="px-4 py-4">
                      <div className="font-medium tracking-wide text-slate-600 dark:text-customHeading">
                        {account.accountNumber}
                      </div>
                    </td> */}
                    <td className="px-4 py-4">
                      <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
                        {account.createdDate}
                      </span>
                    </td>
                    <td className="rounded-r-3xl px-4 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit?.(account)}
                          className="inline-flex items-center gap-1 rounded-xl bg-customAccent/10 px-3 py-2 text-xs font-semibold text-customAccent transition hover:bg-customAccent/20"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete?.(account)}
                          className="inline-flex items-center gap-1 rounded-xl bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-500 transition hover:bg-rose-500/20"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export default AccountTable
